import {useCallback, useEffect, useRef, useState} from 'react'

// "Position photo" tool (2026-10-06). The app always crops banner and tile
// photos from the centre, so a photo whose subject is off-centre loses it.
// Here you drag/zoom the photo inside a frame with the exact shape the app
// shows, and we save that crop as the image - so the app shows exactly
// what you framed, with no app update needed.
//
// Shapes measured from the app (phone first, iPad as a dashed guide):
export const PHOTO_SHAPES = {
  banner: {
    label: 'Home screen banner',
    aspect: 4.4, // phone: full width x 20% of width
    outputWidth: 1760,
    guides: [{label: 'iPad shows this band', aspect: 7.5}],
  },
  tile: {
    label: 'Category tile',
    aspect: 1.68, // phone: 42% x 25% of screen width
    outputWidth: 1100,
    guides: [{label: 'iPad shows this band', aspect: 2.6}],
  },
}

const FRAME_W = 640

/**
 * source: a File (new upload) or an image URL (reposition an existing one).
 * shape:  PHOTO_SHAPES key.
 * onDone(file) gets a JPEG File of the framed crop; onCancel closes.
 */
const ImageCropModal = ({source, shape = 'banner', onCancel, onDone}) => {
  const cfg = PHOTO_SHAPES[shape] || PHOTO_SHAPES.banner
  const frameH = Math.round(FRAME_W / cfg.aspect)
  const canvasRef = useRef(null)
  const [img, setImg] = useState(null)
  const [error, setError] = useState('')
  const [zoom, setZoom] = useState(1)
  // centre of the frame, in image pixels
  const [center, setCenter] = useState({x: 0, y: 0})
  const drag = useRef(null)
  const [saving, setSaving] = useState(false)

  // Load the image (cross-origin for existing S3 photos so we can re-crop).
  useEffect(() => {
    let url = ''
    const im = new Image()
    im.crossOrigin = 'anonymous'
    im.onload = () => {
      setImg(im)
      setZoom(1)
      setCenter({x: im.naturalWidth / 2, y: im.naturalHeight / 2})
    }
    im.onerror = () =>
      setError(
        typeof source === 'string'
          ? 'Could not load this photo for repositioning (the photo storage may not allow it yet). You can still upload the original photo again and position it.'
          : 'Could not read this image file.'
      )
    if (typeof source === 'string') {
      // cache-buster so the browser fetches it with CORS headers
      im.src = source + (source.includes('?') ? '&' : '?') + 'crop=' + Date.now()
    } else if (source) {
      url = URL.createObjectURL(source)
      im.src = url
    }
    return () => url && URL.revokeObjectURL(url)
  }, [source])

  // Scale so the photo always covers the frame at zoom 1.
  const scaleFor = useCallback(
    (fw, fh, z) => (img ? Math.max(fw / img.naturalWidth, fh / img.naturalHeight) * z : 1),
    [img]
  )

  const clamp = useCallback(
    (c, z) => {
      if (!img) return c
      const s = scaleFor(FRAME_W, frameH, z)
      const halfW = FRAME_W / 2 / s
      const halfH = frameH / 2 / s
      return {
        x: Math.min(Math.max(c.x, halfW), img.naturalWidth - halfW),
        y: Math.min(Math.max(c.y, halfH), img.naturalHeight - halfH),
      }
    },
    [img, frameH, scaleFor]
  )

  const draw = useCallback(
    (ctx, w, h) => {
      const s = scaleFor(w, h, zoom)
      ctx.fillStyle = '#eee'
      ctx.fillRect(0, 0, w, h)
      ctx.drawImage(img, w / 2 - center.x * s, h / 2 - center.y * s, img.naturalWidth * s, img.naturalHeight * s)
    },
    [img, zoom, center, scaleFor]
  )

  // Preview
  useEffect(() => {
    const cv = canvasRef.current
    if (!cv || !img) return
    const dpr = window.devicePixelRatio || 1
    cv.width = FRAME_W * dpr
    cv.height = frameH * dpr
    const ctx = cv.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    draw(ctx, FRAME_W, frameH)
    // dashed guides for other screen sizes (centre band)
    ctx.setLineDash([6, 5])
    ctx.lineWidth = 1.5
    ctx.strokeStyle = 'rgba(255,255,255,0.95)'
    ctx.font = '12px sans-serif'
    ctx.fillStyle = 'rgba(255,255,255,0.95)'
    for (const g of cfg.guides) {
      const gh = FRAME_W / g.aspect
      const y = (frameH - gh) / 2
      ctx.strokeRect(1, y, FRAME_W - 2, gh)
      ctx.fillText(g.label, 8, y + 14)
    }
  }, [img, draw, frameH, cfg.guides])

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    drag.current = {x: e.clientX, y: e.clientY, c: center}
  }
  const onPointerMove = (e) => {
    if (!drag.current || !img) return
    const s = scaleFor(FRAME_W, frameH, zoom)
    // canvas may be shown smaller than FRAME_W on narrow screens
    const ratio = FRAME_W / (e.currentTarget.getBoundingClientRect().width || FRAME_W)
    const dx = ((e.clientX - drag.current.x) * ratio) / s
    const dy = ((e.clientY - drag.current.y) * ratio) / s
    setCenter(clamp({x: drag.current.c.x - dx, y: drag.current.c.y - dy}, zoom))
  }
  const onPointerUp = () => (drag.current = null)
  const onZoom = (z) => {
    setZoom(z)
    setCenter((c) => clamp(c, z))
  }

  const save = async () => {
    if (!img) return
    setSaving(true)
    try {
      const ow = cfg.outputWidth
      const oh = Math.round(ow / cfg.aspect)
      const out = document.createElement('canvas')
      out.width = ow
      out.height = oh
      draw(out.getContext('2d'), ow, oh)
      const blob = await new Promise((resolve, reject) => {
        try {
          out.toBlob((b) => (b ? resolve(b) : reject(new Error('empty'))), 'image/jpeg', 0.86)
        } catch (err) {
          reject(err)
        }
      })
      const base = (typeof source === 'string' ? 'photo' : source.name.replace(/\.\w+$/, '')) || 'photo'
      await onDone(new File([blob], `${base}-${shape}-${Date.now()}.jpg`, {type: 'image/jpeg'}))
    } catch (err) {
      setError(
        'Could not save this crop. If you were repositioning an existing photo, the photo storage may not allow it yet; upload the original photo again instead.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16}}
      onMouseDown={(e) => e.target === e.currentTarget && !saving && onCancel()}
    >
      <div className='bg-white rounded p-7' style={{width: FRAME_W + 56, maxWidth: '100%'}}>
        <h3 className='fw-bolder mb-1'>Position photo</h3>
        <p className='text-muted fs-7 mb-4'>
          {cfg.label}: drag the photo to move it and use the slider to zoom. This frame is exactly
          what phones show; the dashed band is what iPads show, so keep the main subject inside it.
        </p>
        {error ? (
          <div className='alert alert-warning py-3'>{error}</div>
        ) : !img ? (
          <div className='text-muted py-10 text-center'>Loading photo…</div>
        ) : (
          <>
            <canvas
              ref={canvasRef}
              style={{width: FRAME_W, maxWidth: '100%', height: 'auto', aspectRatio: `${FRAME_W} / ${frameH}`, cursor: 'grab', touchAction: 'none', borderRadius: 8, display: 'block'}}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
            <div className='d-flex align-items-center gap-3 mt-4'>
              <span className='text-muted fs-7'>Zoom</span>
              <input
                type='range'
                className='form-range'
                min='1'
                max='4'
                step='0.01'
                value={zoom}
                onChange={(e) => onZoom(Number(e.target.value))}
              />
            </div>
          </>
        )}
        <div className='d-flex justify-content-end gap-2 mt-5'>
          <button className='btn btn-sm btn-light' disabled={saving} onClick={onCancel}>
            Cancel
          </button>
          <button className='btn btn-sm btn-primary' disabled={!img || !!error || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ImageCropModal
